/**
 * SPA hooks for owner-only team management — /api/team (see
 * apps/api/src/routes/team.ts). Renders in SettingsPage's TeamCard, gated
 * on `me.user.role === 'owner'` there.
 *
 * `VA_PERMISSIONS` is the frontend mirror of the API's whitelist
 * (middleware/auth.ts's `VA_PERMISSION_PATHS` → `VA_PERMISSION_KEYS`). It's
 * a hand-kept constant, not fetched, because the whitelist is currently a
 * single entry and the API 400s on anything not in it anyway — this only
 * drives which checkboxes the "Add VA" dialog renders.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ListTeamResponse,
  TeamMember,
  CreateTeamMemberResponse,
  type CreateTeamMemberRequest,
  type UpdateTeamMemberRequest,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export const TEAM_KEY = ['team'] as const;

export const VA_PERMISSIONS: { key: string; label: string }[] = [
  { key: 'planning', label: 'Planning' },
];

export function useTeam(enabled = true) {
  return useQuery({
    queryKey: TEAM_KEY,
    enabled,
    queryFn: () => apiFetch('/api/team', { responseSchema: ListTeamResponse }),
  });
}

export function useCreateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTeamMemberRequest) =>
      apiFetch('/api/team', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreateTeamMemberResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEAM_KEY });
    },
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateTeamMemberRequest & { id: string }) =>
      apiFetch(`/api/team/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: TeamMember,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEAM_KEY });
    },
  });
}

export function useDeleteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ id: string }>(`/api/team/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEAM_KEY });
    },
  });
}
